import React, { useMemo, useRef } from 'react';
import classNames from 'classnames/bind';
import { withRenderFlag } from '@beef/react';
import { Button as _Button, Tag as _Tag, Text as _Text } from '@carnica/ui-kit';
import type { ButtonProps, TagProps, TextProps } from '@carnica/ui-kit/types';
import { LegalBadge } from '@carnica/smart-kit';

import { ButtonArrow as _ButtonArrow, ButtonLinkType } from 'pages/MainPage/ui/ButtonArrow';
import { Banner as UIBanner } from 'pages/MainPage/ui/Banner';
import { AdaptivityContainer } from 'pages/MainPage/utils/ResponsiveContainers';
import { MenuUrlTarget } from 'pages/MainPage/utils/utils';
import { Metric } from 'pages/MainPage/metric';

import { IMainCarouselItem } from '../types';
import { FullWidthBanner } from '../../../ui/Banner/FullWidthBanner';
import styles from '../styles.pcss';

const cx = classNames.bind(styles);

const Tag: React.FC<TagProps> = withRenderFlag(_Tag);
const Text: React.FC<TextProps> = withRenderFlag(_Text);
const Button: React.FC<ButtonProps> = withRenderFlag(_Button);
const ButtonArrow: React.FC<ButtonLinkType> = withRenderFlag(_ButtonArrow);

export const MainCarouselItem: React.FC<IMainCarouselItem> = ({
  item,
  bannerId,
  bannerPosition,
  visibility = true,
}) => {
  // для закрытия внтури слайдера во время перелистывания
  const bannerRef = useRef<HTMLAnchorElement>(null);

  const metricEvent = useMemo(() => {
    return visibility ? 'handleTopBannerClick' : 'handleNotSupport';
  }, [visibility]);

  return (
    <Metric
      data={JSON.stringify({
        bannerTitle: item.heading?.text,
        bannerDescription: item.subtitle?.text,
        bannerUrl: item.link?.href,
        bannerId,
        bannerPosition,
      })}
      events={{ commonMetric: ['handleTopBannerView'] }}
      mode="viewport"
      once
      threshold={0.2}
    >
      <AdaptivityContainer minWidth={1920}>
        <Metric
          asChild
          data={JSON.stringify({
            bannerTitle: item.heading?.text,
            bannerDescription: item.subtitle?.text,
            bannerUrl: item.link?.href,
            bannerId,
            bannerPosition,
          })}
          events={{ commonMetric: [metricEvent] }}
          mode="click"
        >
          <a
            aria-label={item.heading?.text}
            href={item.link?.href}
            ref={bannerRef}
            target={MenuUrlTarget.get(item.link?.isBlankUrlTarget as boolean)}
          >
            <FullWidthBanner
              contentClassName={cx('carousel-slide-content')}
              image={item.image ? <img alt="" src={item.image?.xl?.url} /> : null}
              imageStyle={item.image?.xl?.imageStyle}
              legal={
                item.legal && (
                  /* eslint-disable-next-line react/jsx-no-useless-fragment */
                  <>
                    {item.legal.type === 'text' ?
                      <Text
                        color={item.legal.color}
                        shouldRender={item.legal.text}
                        variant={item.legal.variant}
                      >
                        {item.legal.text}
                      </Text>
                    : <LegalBadge {...item.legal} containerRef={bannerRef} />}
                  </>
                )
              }
              style={{
                backgroundImage: `url(${item.background?.xl?.url})`,
                backgroundColor: item.background?.xl?.color,
                backgroundSize: item.background?.xl?.size,
                backgroundPosition: item.background?.xl?.position,
              }}
            >
              {item.logo && <img alt="" src={item.logo} />}
              <Tag
                className={cx('tag')}
                shouldRender={item.sticky}
                size="m"
                view={item.sticky?.color}
              >
                {item.sticky?.text}
              </Tag>
              <Text
                className={cx('heading')}
                color={item.heading?.color}
                shouldRender={item.heading}
                variant="display-large"
              >
                {item.heading?.text}
              </Text>
              <Text
                className={cx('subtitle')}
                color={item.subtitle?.color}
                shouldRender={item.subtitle}
                variant="body-large"
              >
                {item.subtitle?.text}
              </Text>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButton?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <Button
                  className={cx('button')}
                  href={item.actionButton?.href}
                  shouldRender={item.actionButton}
                  size="m"
                  tagName="a"
                  target={MenuUrlTarget.get(item.actionButton?.isBlankUrlTarget as boolean)}
                  view={item.actionButton?.view}
                >
                  {item.actionButton?.label}
                </Button>
              </Metric>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButtonArrow?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <ButtonArrow
                  className={cx('button')}
                  shouldRender={item.actionButtonArrow}
                  {...item.actionButtonArrow}
                  aria-label="Предыдущий слайд"
                >
                  <Text
                    color={item.actionButtonArrow?.textColor}
                    shouldRender={item.actionButtonArrow}
                    variant="body-accent-small"
                  >
                    {item.actionButtonArrow?.label}
                  </Text>
                </ButtonArrow>
              </Metric>
            </FullWidthBanner>
          </a>
        </Metric>
      </AdaptivityContainer>
      <AdaptivityContainer maxWidth={1919} minWidth={1440}>
        <Metric
          asChild
          data={JSON.stringify({
            bannerTitle: item.heading?.text,
            bannerDescription: item.subtitle?.text,
            bannerUrl: item.link?.href,
            bannerId,
            bannerPosition,
          })}
          events={{ commonMetric: [metricEvent] }}
          mode="click"
        >
          <a
            aria-label={item.heading?.text}
            href={item.link?.href}
            ref={bannerRef}
            target={MenuUrlTarget.get(item.link?.isBlankUrlTarget as boolean)}
          >
            <FullWidthBanner
              contentClassName={cx('carousel-slide-content')}
              image={item.image ? <img alt="" src={item.image?.l?.url} /> : null}
              imageStyle={item.image?.l?.imageStyle}
              legal={
                item.legal && (
                  /* eslint-disable-next-line react/jsx-no-useless-fragment */
                  <>
                    {item.legal.type === 'text' ?
                      <Text
                        color={item.legal.color}
                        shouldRender={item.legal.text}
                        variant={item.legal.variant}
                      >
                        {item.legal.text}
                      </Text>
                    : <LegalBadge {...item.legal} containerRef={bannerRef} />}
                  </>
                )
              }
              style={{
                backgroundImage: `url(${item.background?.l?.url})`,
                backgroundColor: item.background?.l?.color,
                backgroundSize: item.background?.l?.size,
                backgroundPosition: item.background?.l?.position,
              }}
            >
              {item.logo && <img alt="" src={item.logo} />}
              <Tag
                className={cx('tag')}
                shouldRender={item.sticky}
                size="m"
                view={item.sticky?.color}
              >
                {item.sticky?.text}
              </Tag>
              <Text
                className={cx('heading')}
                color={item.heading?.color}
                shouldRender={item.heading}
                variant="display-large"
              >
                {item.heading?.text}
              </Text>
              <Text
                className={cx('subtitle')}
                color={item.subtitle?.color}
                shouldRender={item.subtitle}
                variant="body-large"
              >
                {item.subtitle?.text}
              </Text>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButton?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <Button
                  className={cx('button')}
                  href={item.actionButton?.href}
                  shouldRender={item.actionButton}
                  size="m"
                  tagName="a"
                  target={MenuUrlTarget.get(item.actionButton?.isBlankUrlTarget as boolean)}
                  view={item.actionButton?.view}
                >
                  {item.actionButton?.label}
                </Button>
              </Metric>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButtonArrow?.label,
                  bannerUrl: item.link?.href,
                  bannerId,
                  bannerDescription: item.subtitle?.text,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <ButtonArrow
                  className={cx('button')}
                  shouldRender={item.actionButtonArrow}
                  {...item.actionButtonArrow}
                  aria-label="Следующий слайд"
                >
                  <Text
                    color={item.actionButtonArrow?.textColor}
                    shouldRender={item.actionButtonArrow}
                    variant="body-accent-small"
                  >
                    {item.actionButtonArrow?.label}
                  </Text>
                </ButtonArrow>
              </Metric>
            </FullWidthBanner>
          </a>
        </Metric>
      </AdaptivityContainer>
      <AdaptivityContainer maxWidth={1439} minWidth={1025}>
        <Metric
          asChild
          data={JSON.stringify({
            bannerTitle: item.heading?.text,
            bannerDescription: item.subtitle?.text,
            bannerUrl: item.link?.href,
            bannerId,
            bannerPosition,
          })}
          events={{ commonMetric: [metricEvent] }}
          mode="click"
        >
          <a
            aria-label={item.heading?.text}
            href={item.link?.href}
            ref={bannerRef}
            target={MenuUrlTarget.get(item.link?.isBlankUrlTarget as boolean)}
          >
            <FullWidthBanner
              contentClassName={cx('carousel-slide-content')}
              image={item.image ? <img alt="" src={item.image?.m?.url} /> : null}
              imageStyle={item.image?.m?.imageStyle}
              legal={
                item.legal && (
                  /* eslint-disable-next-line react/jsx-no-useless-fragment */
                  <>
                    {item.legal.type === 'text' ?
                      <Text
                        color={item.legal.color}
                        shouldRender={item.legal.text}
                        variant={item.legal.variant}
                      >
                        {item.legal.text}
                      </Text>
                    : <LegalBadge {...item.legal} containerRef={bannerRef} />}
                  </>
                )
              }
              style={{
                backgroundImage: `url(${item.background?.m?.url})`,
                backgroundColor: item.background?.m?.color,
                backgroundSize: item.background?.m?.size,
                backgroundPosition: item.background?.m?.position,
              }}
            >
              {item.logo && <img alt="" src={item.logo} />}
              <Tag shouldRender={item.sticky} size="m" view={item.sticky?.color}>
                {item.sticky?.text}
              </Tag>
              <Text color={item.heading?.color} shouldRender={item.heading} variant="display-small">
                {item.heading?.text}
              </Text>
              <Text color={item.subtitle?.color} shouldRender={item.subtitle} variant="body-small">
                {item.subtitle?.text}
              </Text>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButton?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <Button
                  href={item.actionButton?.href}
                  shouldRender={item.actionButton}
                  size="m"
                  tagName="a"
                  target={MenuUrlTarget.get(item.actionButton?.isBlankUrlTarget as boolean)}
                  view={item.actionButton?.view}
                >
                  {item.actionButton?.label}
                </Button>
              </Metric>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButtonArrow?.label,
                  bannerDescription: item.subtitle?.text,
                  bannerUrl: item.link?.href,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <ButtonArrow
                  shouldRender={item.actionButtonArrow}
                  {...item.actionButtonArrow}
                  aria-label="Предыдущий слайд"
                >
                  <Text
                    color={item.actionButtonArrow?.textColor}
                    shouldRender={item.actionButtonArrow}
                    variant="body-accent-small"
                  >
                    {item.actionButtonArrow?.label}
                  </Text>
                </ButtonArrow>
              </Metric>
            </FullWidthBanner>
          </a>
        </Metric>
      </AdaptivityContainer>
      <AdaptivityContainer maxWidth={1024} minWidth={801}>
        <Metric
          asChild
          data={JSON.stringify({
            bannerTitle: item.heading?.text,
            bannerDescription: item.subtitle?.text,
            bannerUrl: item.link?.href,
            bannerId,
            bannerPosition,
          })}
          events={{ commonMetric: [metricEvent] }}
          mode="click"
        >
          <a
            aria-label={item.heading?.text}
            href={item.link?.href}
            ref={bannerRef}
            target={MenuUrlTarget.get(item.link?.isBlankUrlTarget as boolean)}
          >
            <FullWidthBanner
              image={item.image ? <img alt="" src={item.image?.s?.url} /> : null}
              imageStyle={item.image?.s?.imageStyle}
              legal={
                item.legal && (
                  /* eslint-disable-next-line react/jsx-no-useless-fragment */
                  <>
                    {item.legal.type === 'text' ?
                      <Text
                        color={item.legal.color}
                        shouldRender={item.legal.text}
                        variant={item.legal.variant}
                      >
                        {item.legal.text}
                      </Text>
                    : <LegalBadge {...item.legal} containerRef={bannerRef} />}
                  </>
                )
              }
              style={{
                backgroundImage: `url(${item.background?.s?.url})`,
                backgroundColor: item.background?.s?.color,
                backgroundSize: item.background?.s?.size,
                backgroundPosition: item.background?.s?.position,
              }}
            >
              {item.logo && <img alt="" src={item.logo} />}
              <Tag shouldRender={item.sticky} size="m" view={item.sticky?.color}>
                {item.sticky?.text}
              </Tag>
              <Text color={item.heading?.color} shouldRender={item.heading} variant="display-small">
                {item.heading?.text}
              </Text>
              <Text color={item.subtitle?.color} shouldRender={item.subtitle} variant="body-small">
                {item.subtitle?.text}
              </Text>

              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButton?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <Button
                  href={item.actionButton?.href}
                  shouldRender={item.actionButton}
                  size="m"
                  tagName="a"
                  target={MenuUrlTarget.get(item.actionButton?.isBlankUrlTarget as boolean)}
                  view={item.actionButton?.view}
                >
                  {item.actionButton?.label}
                </Button>
              </Metric>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButtonArrow?.label,
                  bannerDescription: item.subtitle?.text,
                  bannerUrl: item.link?.href,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <ButtonArrow
                  shouldRender={item.actionButtonArrow}
                  {...item.actionButtonArrow}
                  aria-label="Следующий слайд"
                >
                  <Text
                    color={item.actionButtonArrow?.textColor}
                    shouldRender={item.actionButtonArrow}
                    variant="body-accent-small"
                  >
                    {item.actionButtonArrow?.label}
                  </Text>
                </ButtonArrow>
              </Metric>
            </FullWidthBanner>
          </a>
        </Metric>
      </AdaptivityContainer>
      <AdaptivityContainer maxWidth={800}>
        <Metric
          asChild
          data={JSON.stringify({
            bannerTitle: item.heading?.text,
            bannerDescription: item.subtitle?.text,
            bannerUrl: item.link?.href,
            bannerId,
            bannerPosition,
          })}
          events={{ commonMetric: [metricEvent] }}
          mode="click"
        >
          <a
            aria-label={item.heading?.text}
            href={item.link?.href}
            ref={bannerRef}
            target={MenuUrlTarget.get(item.link?.isBlankUrlTarget as boolean)}
          >
            <UIBanner.MobileBanner
              legal={
                item.legal && (
                  /* eslint-disable-next-line react/jsx-no-useless-fragment */
                  <>
                    {item.legal.type === 'text' ?
                      <Text
                        color={item.legal.color}
                        shouldRender={item.legal.text}
                        variant={item.legal.variant}
                      >
                        {item.legal.text}
                      </Text>
                    : <LegalBadge {...item.legal} containerRef={bannerRef} />}
                  </>
                )
              }
              style={{
                backgroundImage: `url(${item.background?.xs?.url})`,
                backgroundColor: item.background?.xs?.color,
                backgroundSize: item.background?.xs?.size,
                backgroundPosition: item.background?.xs?.position,
              }}
            >
              {item.logo && <img alt="" src={item.logo} />}
              <Tag shouldRender={item.sticky} size="m" view={item.sticky?.color}>
                {item.sticky?.text}
              </Tag>
              <Text color={item.heading?.color} shouldRender={item.heading} variant="display-small">
                {item.heading?.text}
              </Text>
              <Text color={item.subtitle?.color} shouldRender={item.subtitle} variant="body-medium">
                {item.subtitle?.text}
              </Text>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButton?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <Button
                  href={item.actionButton?.href}
                  shouldRender={item.actionButton}
                  size="m"
                  tagName="a"
                  target={MenuUrlTarget.get(item.actionButton?.isBlankUrlTarget as boolean)}
                  view={item.actionButton?.view}
                >
                  {item.actionButton?.label}
                </Button>
              </Metric>
              <Metric
                asChild
                data={JSON.stringify({
                  bannerTitle: item.heading?.text,
                  buttonText: item.actionButtonArrow?.label,
                  bannerUrl: item.link?.href,
                  bannerDescription: item.subtitle?.text,
                  bannerId,
                  bannerPosition,
                })}
                events={{ commonMetric: ['handleTopBannerButton'] }}
                mode="click"
              >
                <ButtonArrow
                  shouldRender={item.actionButtonArrow}
                  {...item.actionButtonArrow}
                  aria-label="Следующий слайд"
                >
                  <Text
                    color={item.actionButtonArrow?.textColor}
                    shouldRender={item.actionButtonArrow}
                    variant="body-accent-small"
                  >
                    {item.actionButtonArrow?.label}
                  </Text>
                </ButtonArrow>
              </Metric>
            </UIBanner.MobileBanner>
          </a>
        </Metric>
      </AdaptivityContainer>
    </Metric>
  );
};
